/* Mike Sport menu events and cart attribution. Scoped to the new menu. */
(() => {
  if (window.__msMenuAnalyticsReady) return;
  window.__msMenuAnalyticsReady = true;

  const cookieName = '_ms_menu_assist_at';
  const rootKeys = new Set(['men', 'women', 'kids', 'sports', 'sports-equipment', 'brands', 'sale']);
  const route = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  let syncing = null;

  const readPending = () => {
    const encoded = document.cookie.split('; ').find(part => part.startsWith(`${cookieName}=`))?.slice(cookieName.length + 1);
    if (!encoded) return null;
    try {
      const value = decodeURIComponent(encoded);
      return Number.isFinite(Date.parse(value)) ? value : null;
    } catch (_) { return null; }
  };
  const writePending = value => {
    document.cookie = `${cookieName}=${encodeURIComponent(value)}; Max-Age=86400; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
  };
  const clearPending = () => {
    document.cookie = `${cookieName}=; Max-Age=0; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
  };
  const push = (event, details = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...details });
  };
  const destination = element => {
    if (element.hasAttribute('data-ms-next') || element.hasAttribute('data-ms-tab')) return 'submenu';
    const href = element.getAttribute('href');
    if (!href || href === '#') return 'placeholder';
    try {
      const url = new URL(href, location.href);
      if (url.origin !== location.origin) return 'external';
      if (/\/products\/[^/]+/.test(url.pathname)) return 'product';
      if (/\/collections\/[^/]+/.test(url.pathname)) return 'collection';
      if (/\/pages\/[^/]+/.test(url.pathname)) return 'page';
      if (/\/search\/?$/.test(url.pathname)) return 'search';
      return 'internal';
    } catch (_) { return 'external'; }
  };
  const itemLabel = element => (
    element.getAttribute('aria-label') ||
    element.querySelector('strong')?.textContent ||
    element.querySelector('span:not(.ms-mobile__arrow)')?.textContent ||
    element.textContent ||
    element.querySelector('img')?.alt || ''
  ).replace(/\s+/g, ' ').replace(/\s*›\s*$/, '').trim().slice(0, 160);
  const itemDepth = (element, root) => {
    if (element.matches('[data-ms-tab]')) return 1;
    if (root.matches('.ms-nav')) {
      return element.closest('.ms-nav__sport-column-links,.ms-nav__equipment-links') ? 3 : 2;
    }
    const screen = element.closest('[data-ms-screen]')?.dataset.msScreen;
    return screen === 'main' ? 1 : rootKeys.has(screen) ? 2 : 3;
  };
  const stamp = root => {
    const selector = root.matches('.ms-nav') ? '[data-ms-tab],.ms-nav__panel a[href],.ms-nav__panel button[data-ms-next]' : '.ms-mobile__screen a[href],.ms-mobile__screen button[data-ms-next]';
    root.querySelectorAll(selector).forEach(element => {
      if (element.hasAttribute('data-menu-level')) return;
      const parent = element.parentElement;
      const peers = [...parent.children].filter(child => child.matches('a[href],button[data-ms-next],button[data-ms-tab]'));
      element.setAttribute('data-menu-level', String(itemDepth(element, root)));
      element.setAttribute('data-menu-destination-type', destination(element));
      element.setAttribute('data-menu-item-label', itemLabel(element));
      element.setAttribute('data-menu-item-position', String(peers.indexOf(element) + 1));
    });
  };

  const syncCart = () => {
    const at = readPending();
    if (!at || syncing) return syncing || Promise.resolve();
    syncing = fetch(`${route}cart.js`, { credentials: 'same-origin', cache: 'no-store' })
      .then(response => { if (!response.ok) throw new Error('Cart read failed'); return response.json(); })
      .then(cart => {
        if (!cart.item_count) return;
        if (cart.attributes?._menu_assisted === 'true') { clearPending(); return; }
        return fetch(`${route}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _menu_assisted: 'true', _menu_assisted_at: at } })
        }).then(response => {
          if (!response.ok) throw new Error('Cart update failed');
          clearPending();
        });
      }).catch(() => { /* Keep the cookie for the next attempt or page. */ })
      .finally(() => { syncing = null; });
    return syncing;
  };
  const assist = () => {
    if (!readPending()) writePending(new Date().toISOString());
    syncCart();
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-ms-menu],[data-ms-mobile]').forEach(root => {
      if (root.dataset.msAnalyticsReady) return;
      root.dataset.msAnalyticsReady = 'true';
      stamp(root);
      const desktop = root.matches('.ms-nav');
      const state = { open: false, key: null, tapped: false };
      const current = () => desktop
        ? root.querySelector('[data-ms-tab][aria-expanded="true"]')?.dataset.msTab || null
        : root.querySelector('.ms-mobile__open[aria-expanded="true"]') ? 'mobile' : null;
      const checkState = () => {
        const key = current();
        if (!key && state.open) {
          if (!state.tapped) push('menu_closed_no_tap', { menu_name: state.key, menu_device: desktop ? 'desktop' : 'mobile' });
          state.open = false; state.key = null; state.tapped = false;
        } else if (key && (key !== state.key || !state.open)) {
          state.open = true; state.key = key; state.tapped = false;
          push('menu_opened', { menu_name: key, menu_device: desktop ? 'desktop' : 'mobile' });
        }
      };
      new MutationObserver(checkState).observe(root, { subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
      root.addEventListener('click', event => {
        const item = event.target.closest('[data-menu-level]');
        if (!item || !root.contains(item)) return;
        const depth = Number(item.dataset.menuLevel);
        const type = item.dataset.menuDestinationType;
        push('menu_item_tapped', {
          depth,
          destination: type,
          label: item.dataset.menuItemLabel,
          position: Number(item.dataset.menuItemPosition),
          menu_name: desktop ? item.closest('[data-ms-panel]')?.dataset.msPanel || item.dataset.msTab : item.closest('[data-ms-screen]')?.dataset.msScreen || 'main',
          menu_device: desktop ? 'desktop' : 'mobile'
        });
        if (depth > 1) state.tapped = true;
        if (type === 'product' || type === 'collection') assist();
      }, true);
      checkState();
    });
  };

  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('pageshow', syncCart);
  if (readPending()) {
    syncCart();
    setTimeout(syncCart, 1500);
    setTimeout(syncCart, 5000);
  }
  if (typeof PerformanceObserver !== 'undefined') {
    try {
      const observer = new PerformanceObserver(list => {
        if (readPending() && list.getEntries().some(entry => /\/cart\/add(?:\.js)?(?:\?|$)/.test(entry.name))) setTimeout(syncCart, 500);
      });
      observer.observe({ entryTypes: ['resource'] });
    } catch (_) { /* Older browsers still sync on the next page. */ }
  }
})();
